//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { Box, Button, Typography } from '@mui/material';

import { type AgentPage, type UiAction } from '@/agent/contract';
import { routeForPage } from '@/agent/transport';

interface UnappliedActionProps {
  action: UiAction;
  onNavigate: (path: string) => void;
}

const PAGE_TITLE: Record<Exclude<AgentPage, 'other'>, string> = {
  graphs: 'Graphs',
  dashboard: 'Dashboard',
  datasets: 'Datasets',
  explorer: 'Explorer',
  jobs: 'Jobs'
};

// eslint-disable-next-line react-refresh/only-export-components
export const describeAction = (action: UiAction): string => {
  switch (action.kind) {
    case 'show_draft':
      return `show draft version ${action.version} in the schema editor`;
    case 'start_load':
      return `start loading the sources into ${action.graph}`;
    case 'show_job':
      return `show job ${action.job_id}`;
    case 'navigate':
      return `open ${PAGE_TITLE[action.to]}`;
  }
};

// An action whose page is not on screen is said, not silently dropped and
// never applied to whatever page is showing.
const UnappliedAction = ({ action, onNavigate }: UnappliedActionProps) => {
  // navigate always applies, so only page-targeted actions reach here.
  if (action.page === '*') return null;
  const title = PAGE_TITLE[action.page];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: '17px',
               my: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        On the {title} page this would {describeAction(action)}.
      </Typography>
      <Button size="small" variant="outlined"
              onClick={() => onNavigate(routeForPage(action.page))}>
        Open {title}
      </Button>
    </Box>
  );
};

export default UnappliedAction;
